import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';
import authReducer from './auth/reducer';
import cartReducer from './cart/reducer';
import { cartActions } from './cart/actions';
import rootSaga from './rootSaga';

const CART_STORAGE_PREFIX = 'sedyam_cart';
const LEGACY_CART_STORAGE_KEY = 'sedyam_cart';
const PROFILE_STORAGE_KEY = 'sedyam_profile';
const GUEST_CART_OWNER = 'guest';

const buildOwnerKey = (profile) => {
  if (!profile || typeof profile !== 'object') return GUEST_CART_OWNER;
  const ownerId = String(profile._id || profile.id || profile.email || profile.phone || '').trim();
  return ownerId ? `user:${ownerId.toLowerCase()}` : GUEST_CART_OWNER;
};

const buildCartStorageKey = (ownerKey) => `${CART_STORAGE_PREFIX}:${ownerKey || GUEST_CART_OWNER}`;

const isLegacyDealItem = (item = {}) =>
  item.itemType !== 'package' &&
  item.dealId &&
  item.dealName &&
  String(item.cartKey || '').startsWith(`deal:${item.dealId}:`);

const normalizeLegacyDealItems = (items = []) => {
  const dealGroups = new Map();
  const ordered = [];

  items.forEach((item) => {
    if (!isLegacyDealItem(item)) {
      ordered.push(item);
      return;
    }

    const dealKey = String(item.dealId);
    if (!dealGroups.has(dealKey)) {
      const group = { dealId: dealKey, items: [] };
      dealGroups.set(dealKey, group);
      ordered.push(group);
    }
    dealGroups.get(dealKey).items.push(item);
  });

  return ordered.flatMap((entry) => {
    if (!entry?.items) return [entry];

    const itemsInDeal = entry.items;
    const quantities = itemsInDeal.map((item) => Math.max(1, Number(item.quantity || 1)));
    const firstQuantity = quantities[0] || 1;
    const packageQuantity = quantities.every((quantity) => quantity === firstQuantity) ? firstQuantity : 1;
    const packageItems = itemsInDeal.map((item) => ({
      ...item,
      quantity: Math.max(1, Number(item.quantity || 1)) / packageQuantity,
    }));
    const originalPrice = packageItems.reduce(
      (sum, item) => sum + Number(item.originalUnitPrice || item.price || 0) * Number(item.quantity || 1),
      0
    );
    const price = packageItems.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
      0
    );
    const firstItem = itemsInDeal[0] || {};

    return [{
      id: entry.dealId,
      cartKey: `package:${entry.dealId}`,
      itemType: 'package',
      name: firstItem.dealName || 'Farm Package',
      image: firstItem.image || '',
      price,
      originalPrice,
      packageSavings: Math.max(0, originalPrice - price),
      dealId: entry.dealId,
      dealName: firstItem.dealName || '',
      dealOccasion: firstItem.dealOccasion || '',
      dealDiscountPercent: Number(firstItem.dealDiscountPercent || 0),
      itemCount: packageItems.length,
      packageItems,
      unitPriceLabel: '/pack',
      quantityLabel: 'packs',
      quantity: packageQuantity,
    }];
  });
};

const readProfileFromStorage = () => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(PROFILE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const readCartItems = (storageKey) => {
  try {
    if (typeof window === 'undefined') return undefined;
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.items) ? normalizeLegacyDealItems(parsed.items) : [];
  } catch {
    return [];
  }
};

const loadCartState = (initialOwnerKey) => ({
  cart: {
    items:
      initialOwnerKey === GUEST_CART_OWNER
        ? []
        : readCartItems(buildCartStorageKey(initialOwnerKey)),
  },
});

const rootReducer = combineReducers({
  auth: authReducer,
  cart: cartReducer,
});

const sagaMiddleware = createSagaMiddleware();

const composeEnhancers =
  (typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

const initialOwnerKey = buildOwnerKey(readProfileFromStorage());

const store = createStore(
  rootReducer,
  loadCartState(initialOwnerKey),
  composeEnhancers(applyMiddleware(sagaMiddleware))
);

sagaMiddleware.run(rootSaga);

if (typeof window !== 'undefined') {
  if (initialOwnerKey === GUEST_CART_OWNER) {
    window.localStorage.removeItem(buildCartStorageKey(GUEST_CART_OWNER));
    window.localStorage.removeItem(LEGACY_CART_STORAGE_KEY);
  }

  let activeOwnerKey = initialOwnerKey;
  let hydratingCart = false;

  store.subscribe(() => {
    try {
      if (hydratingCart) return;

      const state = store.getState();
      const nextOwnerKey = buildOwnerKey(state.auth?.userProfile);

      if (nextOwnerKey !== activeOwnerKey) {
        activeOwnerKey = nextOwnerKey;
        const shouldResetGuestCart = activeOwnerKey === GUEST_CART_OWNER;
        if (shouldResetGuestCart) {
          window.localStorage.removeItem(buildCartStorageKey(GUEST_CART_OWNER));
        }
        const nextItems = shouldResetGuestCart ? [] : readCartItems(buildCartStorageKey(activeOwnerKey));
        hydratingCart = true;
        store.dispatch(cartActions.replaceItems(nextItems));
        hydratingCart = false;
        return;
      }

      window.localStorage.setItem(
        buildCartStorageKey(activeOwnerKey),
        JSON.stringify({
          items: state.cart.items,
        })
      );
      if (activeOwnerKey === GUEST_CART_OWNER) {
        window.localStorage.removeItem(LEGACY_CART_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures and keep app usable.
    } finally {
      hydratingCart = false;
    }
  });
}

export default store;
